import { Box, Divider, Grid, Typography } from '@mui/material';
import { Formik, type FormikContextType } from 'formik';
import { type ReactElement, useContext } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import {
  type ContactFormikType,
  ContactSchema,
  FormikContact,
  NewContactSchema,
} from '@core/formik/contact';
import { Contact } from '@core/types/api/Contact/Contact';
import Enums from '@core/types/api/Enums';
import { FileEntityType } from '@core/types/file';
import { FormErrorNotification } from '@core/utils/formikHelpers';
import { requiredLabel } from '@core/utils/strings';
import { NIL_UUID } from '@core/utils/uuid';
import { DrawerFormFooter } from '@web/components/DrawerFormFooter/DrawerFormFooter';
import { attachBufferedEntityFiles, useFileUploads } from '@web/components/Files';
import { Input } from '@web/components/Input/Input';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '@web/components/LookupInput/ApiLookupInput';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { DrawerCloseHandler } from '@web/components/SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '@web/components/SlabDrawer/SlabDrawer';
import { SlabDrawerHeader } from '@web/components/SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '@web/components/SlabDrawer/useDrawerCloseGuard';
import { useDrawerArchive } from '@web/hooks/useDrawerArchive';
import type { SharedDrawerOverrideProps } from '@web/hooks/useDrawerManager';
import { useSlabFormQuery } from '@web/hooks/useSlabFormQuery';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { useToast } from '@web/providers/ToastProvider/useToast';
import { DefaultEnsureDefined, lookups } from '@web/utils/DomainHelpers';

import { ContactFilesSection } from './ContactFilesSection';

type ContactDrawerProps = SharedDrawerOverrideProps<Contact>;

const ContactDrawerPage = ({
  resourceID,
  setIsOpen,
  onSave,
  onError,
  onClose,
  ensureDefined = DefaultEnsureDefined,
}: ContactDrawerProps): ReactElement | null => {
  const ctx = useContext(SlabContext);
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);

  const requiresContactInfo = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactInfo,
  ]);
  const requiresContactEmail = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagRequiresContactEmail,
  ]);

  const isNew = resourceID === null;

  const filesManager = useFileUploads({ entityType: FileEntityType.Contact, entityId: null });
  const { guardClose, editTracker } = useDrawerCloseGuard();
  const filesToast = useToast({ subjectName: 'file' });

  const createNew = useSlabMutation('POST contact', {
    onSuccess: (data) => {
      attachBufferedEntityFiles(filesManager, data.id, filesToast, FileEntityType.Contact);
      onSave(data);
    },
    onError,
  });
  const updateExisting = useSlabMutation('PUT contact by ID', {
    onSuccess: (data) => onSave(data),
    onError,
  });

  const { confirmAndArchive, isArchiveLoading, resolveIsArchived } = useDrawerArchive({
    entity: 'POST archive contact by ID',
    getIsArchived: (data) => data.isArchived,
    onSuccess: (data) => {
      setIsOpen(false);
      onSave(data);
    },
  });

  const {
    isLoading: isLoadingContact,
    isError: isErrorContact,
    data: maybeContact,
  } = useSlabFormQuery(
    'GET contact by ID',
    {
      pathParams: {
        id: resourceID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const isLoading = isLoadingContact;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isError = isErrorContact;

  if (isError) {
    return <div>ERROR</div>;
  }

  const contact = ensureDefined(maybeContact);
  const formikContact = FormikContact(contact, usesDispatchCustomer);
  const isArchived = resolveIsArchived(maybeContact?.isArchived ?? false);

  const closeDrawer = (formikBag: FormikContextType<ContactFormikType>): void => {
    formikBag.resetForm({ values: formikContact });
    setIsOpen(false);
    onClose?.();
  };

  const requestClose = (formikBag: FormikContextType<ContactFormikType>): void =>
    guardClose({
      isDirty:
        !isArchived &&
        (editTracker.isEdited() ||
          filesManager.hasAttachableItems ||
          filesManager.hasPendingUploads),
      canSave: formikBag.isValid,
      isSaving: createNew.isPending || updateExisting.isPending,
      onSave: formikBag.submitForm,
      onDiscard: () => closeDrawer(formikBag),
    });

  return (
    <Formik
      validationSchema={NewContactSchema({
        fullContactInfo: requiresContactInfo,
        requiresEmail: requiresContactEmail,
      })}
      initialValues={formikContact}
      onSubmit={async (values: ContactFormikType): Promise<void> => {
        // TODO #2219 Need a NewContactFromForm wrapper for this case
        const wireContact = new Contact(values);
        if (formikContact.id === NIL_UUID) {
          createNew.mutate({
            args: {
              body: wireContact,
            },
            schema: ContactSchema,
          });
        } else {
          updateExisting.mutate({
            args: {
              pathParams: {
                id: formikContact.id,
              },
              body: wireContact,
            },
            schema: ContactSchema,
          });
        }
      }}
    >
      {(formikBag): ReactElement => (
        <SlabDrawerLayout>
          <DrawerCloseHandler onClose={() => requestClose(formikBag)} editTracker={editTracker} />
          <Box>
            <FormErrorNotification />
            <SlabDrawerHeader
              title={`${isNew ? 'Create ' : 'Edit '}a contact`}
              onClose={() => requestClose(formikBag)}
            />
            <Box paddingY='1.25rem' display='flex' flexDirection='column' gap='1rem'>
              <Typography variant='h2'>Personal details</Typography>
              <Grid container spacing={2} wrap='nowrap'>
                <Grid item xs={6}>
                  <Input name='firstName' label='First name*' />
                </Grid>
                <Grid item xs={6}>
                  <Input name='lastName' label='Last name*' />
                </Grid>
              </Grid>
              {/* InputDropdowns must be in their own grid to prevent wrap over-responsiveness. */}
              <Grid container spacing={2} wrap='nowrap'>
                <Grid item xs={6}>
                  <ApiLookupInput
                    formState={formikBag.values}
                    name='company'
                    route={{
                      barrel: 'GET companies',
                      args: (inputText, { constructQueryParams }) =>
                        constructQueryParams(inputText, {
                          filterBy: [
                            {
                              operation: Enums.FilterOperation.Equals,
                              name: 'isArchived',
                              values: [false],
                            },
                          ],
                        }),
                      transform: (companyList): LookupInputOption[] =>
                        lookups({
                          ...companyList,
                          label: (c) => c.name,
                          sublabels: (c) => c.lookupSublabels(usesDispatchCustomer),
                        }),
                    }}
                    label='Company name*'
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input name='jobTitle' label={requiredLabel('Job title', requiresContactInfo)} />
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Box paddingY='1.25rem' display='flex' flexDirection='column' gap='1rem'>
              <Typography variant='h2'>Contact details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Input
                    name='emailAddress'
                    label={requiredLabel('Email', requiresContactInfo || requiresContactEmail)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Input
                    name='phoneNumber'
                    label={requiredLabel('Phone number', requiresContactInfo)}
                    type='tel'
                  />
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Input name='notes' label='Notes (Internal Only)' type='textarea' />
            <ContactFilesSection contactID={resourceID} manager={filesManager} />
          </Box>
          <DrawerFormFooter
            isArchived={isArchived}
            isNew={isNew}
            hasEntity={maybeContact !== undefined}
            isArchiveLoading={isArchiveLoading}
            isSaving={createNew.isPending || updateExisting.isPending}
            isSaveDisabled={!formikBag.dirty || createNew.isPending || updateExisting.isPending}
            isResetDisabled={
              !formikBag.dirty ||
              createNew.isPending ||
              updateExisting.isPending ||
              isArchiveLoading
            }
            onArchive={(): void => {
              if (maybeContact) confirmAndArchive({ id: maybeContact.id, isArchived });
            }}
            onCancel={(): void => requestClose(formikBag)}
            onReset={(): void => formikBag.resetForm({ values: formikContact })}
            onSave={formikBag.submitForm}
          />
        </SlabDrawerLayout>
      )}
    </Formik>
  );
};

export const ContactDrawer = (props: ContactDrawerProps): ReactElement => (
  <SlabDrawer isOpen={props.isOpen} close={() => props.setIsOpen(false)}>
    <ContactDrawerPage {...props} />
  </SlabDrawer>
);
